import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import API from '../api/api';
import { AlertType, Generation, GenerationId, Generations, Mode, UserInput } from '../types/common';
import { wait } from '../utils/commonHelpers';
import { showAlert } from './CommonSlice';
import { RootState } from './store';
// import { setFreeAvailableGenerations } from './UserSlice';

type GenerationSlice = {
  generations: Generations;
  history: Pick<Generation, 'features' | 'texts'>[] | [];
  isHistoryLoading: boolean;
  isGenerationLoading: boolean;
};

const initialState: GenerationSlice = {
  generations: {},
  history: [],
  isHistoryLoading: false,
  isGenerationLoading: false,
};

const GENERATION_REQUEST_COUNT = 30;

export const getGenerationId = createAsyncThunk<void, UserInput, { state: RootState }>(
  'generation/getGenerationId',
  async (input: UserInput, { dispatch, getState }) => {
    try {
      dispatch(setIsGenerationLoading(true));
      const { id, timeout, free_generations_used } = await API.getGenerationId({ ...input, count: 1 });

      const {
        user: { user },
      } = getState();

      // if (user) {
      //   const freeAvailableGenerations = user.free_tier.availableGenerations - free_generations_used;
      //   dispatch(setFreeAvailableGenerations(freeAvailableGenerations));
      // }

      await wait(timeout);
      await dispatch(fetchGeneration({ id, mode: input.mode, timeout }));
    } catch (e: any) {
      dispatch(showAlert({ type: AlertType.Error, message: 'Что-то пошло не так' }));
      console.log('getGenerationId error:', e);
    }
    dispatch(setIsGenerationLoading(false));
  }
);

export const fetchGeneration = createAsyncThunk(
  'generation/fetchGeneration',
  async ({ id, mode, timeout }: { id: GenerationId['id']; mode: Mode; timeout: number }, { dispatch }) => {
    try {
      let generation = await API.fetchGeneration(id);
      let i = 0;

      while ((!generation || generation.message === 'Accepted') && i < GENERATION_REQUEST_COUNT) {
        await wait(timeout + 2500 * i);
        generation = await API.fetchGeneration(id);
        i++;
      }

      if (!generation) {
        dispatch(showAlert({ type: AlertType.Error, message: 'Не удалось сгенерировать. Попробуйте позже' }));
        dispatch(setIsGenerationLoading(false));
        return;
      }

      dispatch(setGeneration({ generation, mode }));
    } catch (e: any) {
      console.log('fetchGeneration error:', e);
      dispatch(showAlert({ type: AlertType.Error, message: 'Не удалось сгенерировать. Попробуйте позже' }));
    }
  }
);

export const fetchHistory = createAsyncThunk('generation/fetchHistory', async (mode: Mode, { dispatch }) => {
  try {
    dispatch(setIsHistoryLoading(true));
    dispatch(setHistory([]));
    const generation = await API.fetchHistory(mode);
    dispatch(setHistory(generation));
  } catch (e: any) {
    dispatch(showAlert({ type: AlertType.Error, message: 'Что-то пошло не так при загрузке истории' }));
  }
  dispatch(setIsHistoryLoading(false));
});

export const generationSlice = createSlice({
  name: 'generation',
  initialState,
  reducers: {
    setIsGenerationLoading: (state, { payload }: { payload: boolean }) => {
      state.isGenerationLoading = payload;
    },

    setIsHistoryLoading: (state, { payload }: { payload: boolean }) => {
      state.isHistoryLoading = payload;
    },

    setHistory: (state, { payload }) => {
      state.history = payload;
    },

    setGeneration: (state, action: PayloadAction<{ generation: any; mode: Mode }>) => {
      const { generation, mode } = action.payload;
      state.generations[mode as keyof GenerationSlice['generations']] = generation;
    },
  },
  extraReducers: (builder) => {},
});

export const { setIsHistoryLoading, setHistory, setGeneration, setIsGenerationLoading } = generationSlice.actions;

export default generationSlice.reducer;
